import { flow /*, observable, computed, action */ } from "mobx"
import { rootStore } from "store/RootStore"
import { TraxisCpeIdMixin } from "store/api/mixin/TraxisCpeIdMixin"
// MTVW-585: 1 retry
import { moment } from "store/util/moment"
import { TIME_FORMAT_BACKEND } from "store/model/Time"
import { urlFixHttpProtocolMismatch } from "store/util/urlFixHttpProtocolMismatch"
import { stopWatch } from "utils/TestSupport"
import { l10n } from "store/lang/L10n"

import { getCoCoModule, wasmToPlainObject } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

// Dictionary mapping snake_case payload keys to camelCase keys natively
const JSON_CAMEL_MAP = {
	channel_id: 'channelId',
	language_id: 'languageId',
	poster_url: 'posterUrl',
	program_type: 'programType',
	replay_guide_category: 'replayGuideCategory',
	season_id: 'seasonId',
	series_collection_id: 'seriesCollectionId',
	series_container: 'seriesContainer',
	series_container_id: 'seriesContainerId',
	series_id: 'seriesId',
	series_name: 'seriesName',
	episode_name: 'episodeName',
	series_recording_id: 'seriesRecordingId',
	series_type: 'seriesType',
	start_millis: 'startMillis',
	end_millis: 'endMillis',
	is_hd: 'isHd',
	is_live: 'isLive',
}

// Wrapper classes with extensions for backward compatibility with some Traxis namings
class Event {
	constructor(event) {
		Object.assign(this, event)
	}

	fake = false

	get dateAndTime() {
		return moment(this.start).format(`dd. DD.MM.YYYY, HH:mm`) + nDash + moment(this.end).format(`HH:mm`)
	}

	get eventTime() {
		return moment(this.start).format(`HH:mm`) + nDash + moment(this.end).format(`HH:mm`)
	}

	get eventType() {
		// MTVW-508
		/*
		const timeCurr = rootStore.time.getTimeStampTick(60),
			timeStart = moment(this.start).utc().valueOf(),
			timeEnd = moment(this.end).utc().valueOf()
		//console.debug("timeCurr %s, timeStart %s, timeEnd %s, %s", timeCurr, timeStart, timeEnd, timeStart <= timeCurr && timeEnd > timeCurr ? "c" : timeStart >= timeCurr ? "n" : "p")
		return timeStart <= timeCurr && timeEnd > timeCurr ? "c" : timeStart >= timeCurr ? "n" : "p"
		*/
		const timeCurr = moment().utc().valueOf()
		return Number(this.startMillis) <= timeCurr && Number(this.endMillis) > timeCurr ? "c" : Number(this.startMillis) >= timeCurr ? "n" : "p"
	}

	get durationInPercent() {
		if (this.eventType !== "c") return 0
		const timeCurr = rootStore.time.getTimeStampTick(60),
			timeStart = moment(this.start).utc().valueOf(),
			timeEnd = moment(this.end).utc().valueOf()
		return parseFloat((100 * (timeCurr - timeStart) / (timeEnd - timeStart)).toFixed(2))
	}

	get AvailabilityDurationInMinutes() {
		return Math.round(moment.duration(moment(Number(this.endMillis)).diff(moment(Number(this.startMillis)))).asMinutes())
	}

	AvailabilityStartDateFormat(format) {
		return moment(Number(this.startMillis)).format(format)
	}
}

class Content {
	constructor(content) {
		Object.assign(this, content)
	}

	// make sure to return secure url
	/*
	get posterUrl() {
		return urlFixHttpProtocolMismatch(this._posterUrl)
	}
	*/
}

class Events {
	event = null
	content = null
	constructor(event, content) {
		this.event = new Event(event)
		this.content = new Content(content)
	}

	get id() {
		return this.event?.id
	}

	get channelId() {
		return this.event?.channelId
	}

	get AvailabilityDurationInMinutes() {
		// GT12 MTVW-487: seconds precision
		//return moment.duration(this.AvailabilityEndDate.diff(this.AvailabilityStartDate)).asMinutes()
		//return Math.round(moment.duration(this.AvailabilityEndDate - this.AvailabilityStartDate).asMinutes())
		return Math.trunc(this.AvailabilityEndDate / 60000) - Math.trunc(this.AvailabilityStartDate / 60000)
		// content.duration is not reliable
		//return moment.duration(this.content?.duration).asMinutes()
	}

	get AvailabilityDurationInMinutesPrecise() {
		return Math.round(moment.duration(this.AvailabilityEndDate - this.AvailabilityStartDate).asMinutes())
	}

	get AvailabilityEndDate() {
		return moment(Number(this.event?.endMillis))
	}

	AvailabilityEndDateFormat(format) {
		return moment(this.AvailabilityEndDateTs).format(format)
	}

	get AvailabilityEndDateTs() {
		return Number(this.event?.endMillis)
	}

	get AvailabilityStartDate() {
		return moment(Number(this.event?.startMillis))
	}

	AvailabilityStartDateFormat(format) {
		return moment(this.AvailabilityStartDateTs).format(format)
	}

	get AvailabilityStartDateTs() {
		return Number(this.event?.startMillis)
	}

	get CurrentDurationInMinutes() {
		return moment.duration(moment(rootStore.time.getTimeStampTick(30)).diff(this.AvailabilityStartDate)).asMinutes()
	}

	get DurationInMinutes() {
		return this.AvailabilityDurationInMinutes
	}

	get CurrentDurationToStartInMinutes() {
		return Math.floor(moment.duration(moment(this.AvailabilityStartDate).diff(rootStore.time.getTimeStampTick(60))).asMinutes())
	}

	get CurrentDurationInPercents() {
		if (this.AvailabilityDurationInMinutesPrecise) {
			return (this.CurrentDurationInMinutes / this.AvailabilityDurationInMinutesPrecise) * 100
		}
		return 0
	}

	isTimeInEventAvailability(time) {
		return this.AvailabilityStartDate.isSameOrBefore(time) && this.AvailabilityEndDate.isAfter(time)
	}

	get isCurrentEvent() {
		return this.getCurrentEventType === "c"
	}

	get getCurrentEventType() {
		const timeCurr = rootStore.time.getTimeStampTick(60),
			timeStart = this.AvailabilityStartDateTs,
			timeEnd = this.AvailabilityEndDateTs
		return timeStart <= timeCurr && timeEnd > timeCurr ? "c" : timeStart >= timeCurr ? "n" : "p"
	}

	get contentId() {
		return this.content?.contentId
	}

	get Title() {
		//return this.content?.Title
		// MTVW-246: For compatibility with traxis model
		return { Name: this.content?.title, SeasonNum: this.content?.season, EpisodeNum: this.content?.episode, EpisodeName: this.content?.episodeName }
	}

	get EpisodeName() {
		return this.content?.episodeName
	}
}

class Program {
	channelId = null
	events = null
}

export class EpgCoco extends TraxisCpeIdMixin {

	/**
	 * Direct JSON string parsing hook using a custom reviver function.
	 * Converts camelCase properties in a single step without allocation explosions.
	 */
	parseJsonToCamelCase(jsonInput) {
		if (typeof jsonInput !== "string") return jsonInput;
		
		return JSON.parse(jsonInput, function(key, value) {
			if (Object.prototype.hasOwnProperty.call(JSON_CAMEL_MAP, key)) {
				this[JSON_CAMEL_MAP[key]] = value;
				return undefined; // Strips out the old snake_case key property
			}
			return value;
		});
	}

	version = flow(function* () {
		let resultJson = yield CoCo.apiFetchEpgVersionRaw()
		// CoCo returns string instead of object, need to convert to object
		if (typeof resultJson === "string") resultJson = JSON.parse(resultJson)
		return `${resultJson["build_tag"]} (${resultJson["build_date"]})`
	})

	programsForDayAsync = flow(function* (day, useEtag) {
		let rawResult = yield CoCo.apiFetchPrograms(BigInt(moment(day).utc().valueOf()), null)
		
		// Memory optimized parse & convert setup combined
		const result = this.parseJsonToCamelCase(rawResult)
		
		const typed = yield CoCo.apiFetchProgramsForOneDayOverAllChannels(day)
		const plainTyped = wasmToPlainObject(typed) || []
		// map data wrapped objects
		const res = plainTyped.map(channelData => {
			const wrapped = new Program()
			wrapped.channelId = channelData.channelId
			// Map inner events
			wrapped.Events = (channelData.events || []).map(
				e => new Events(e.event, e.content)
			)
			return wrapped
		})
		//console.debug("programsForDayAsync raw %o, typed %o", result, res)

		return { progs: res, etag: null }
	})

	// workaround for QLMS-850
	qlms_850(progs, day) {
		if (progs) {
			stopWatch.lap("qlms_850")
			const dayExpectedStart = moment(day).add(-4, 'hour')
			for (let i = 0; i < progs?.length; i++) {
				if (progs[i].Events?.length > 0) {
					if (progs[i].Events[0].AvailabilityStartDateTs > dayExpectedStart.valueOf()) {
						const event = this._fakeEvent(moment(day).add(-4, 'hour'), Number(progs[i].Events[0].event.startMillis))
						progs[i].Events.unshift(event)
					}
				}
			}
			stopWatch.lap("qlms_850 done")
		}
	}

	_fakeEvent(startDate, endDate) {
		const events = new Events()
		const content = new Content()
		content.Title = "..."
		// TODO: currently unused, might need to set correctly in the future
		//content.duration = "PT10M"
		events.content = content
		const event = new Event()
		event.fake = true
		// UNUSED
		//event.channelId = channel
		event.startMillis = startDate
		event.endMillis = endDate
		events.event = event
		return events
	}

	_fakeDayEvents(day, channel) {
		const result = []
		let startHour = -4
		while (startHour < 26) {
			const duration = (Math.random() * 1.0) + 0.25
			const event = this._fakeEvent(moment(day).add(startHour, 'hour').valueOf(), moment(day).add(startHour + duration, 'hour').valueOf())
			startHour = startHour + duration
			result.push(event)
		}
		return result
	}

	fakeProgramsDay(day, channels) {
		const alternate = true // saves considerable time when true
		const progs = []
		if (channels) {
			if (alternate) {
				// we always have to generate events the given day!
				const ch0 = channels?.length > 0 ? this._fakeDayEvents(day, channels[0].id) : []
				const ch1 = channels?.length > 1 ? this._fakeDayEvents(day, channels[1].id) : []
				for (let i = 0; i < channels?.length; i++) {
					const prog = new Program()
					prog.channelId = channels[i].id
					prog.Events = []
					prog.Events = (i % 2 === 0) ? prog.Events.concat(ch0) : prog.Events.concat(ch1)
					progs.push(prog)
				}
			}
			else {
				for (let i = 0; i < channels?.length; i++) {
					const prog = new Program()
					prog.channelId = channels[i].id
					prog.Events = []
					prog.Events = prog.Events.concat(this._fakeDayEvents(day, prog.channelId))
					progs.push(prog)
				}
			}
		}
		return progs
	}

	programsForChannelAsyncAllDays = flow(function* (channel) {
		let progsTyped = yield CoCo.apiFetchProgramsForAllDaysOneChannel(channel)
		progsTyped = wasmToPlainObject(progsTyped)

		const wrapped = new Program()
		wrapped.channelId = progsTyped.channelId
		const startWindow = moment().subtract(7, 'days').startOf('day').utc().valueOf()
		const endWindow = moment().add(7, 'days').endOf('day').utc().valueOf()
		wrapped.events = (progsTyped.events || [])
			.filter(item => {
				const eventTime = Number(item.event.startMillis)	
				// Keep only if it falls within the 14-day window
				return eventTime >= startWindow && eventTime <= endWindow
			})
			.map(item => new Events(item.event, item.content))
		return wrapped
	})

	// MTVW-340
	programsForChannelAsync = flow(function* (channel, day) {
		let coco = yield CoCo.apiFetchProgramsForOneDayOneChannel(day, channel)
		coco = this.parseJsonToCamelCase(coco)
		coco = wasmToPlainObject(coco)
		const res = []
		for (let i = 0; i < coco.length; i++) {
			res.push(new Events(coco[i].event, coco[i].content))
		}
		//console.debug("programsForChannelAsync", day, channel, coco, res)
		return res
	})

	// MTVW-246
	// Live Tv EPG
	programsForNowAsync = flow(function* () {
		let programsRaw = yield CoCo.apiFetchNowPlayingRaw()
		const programsParsed = this.parseJsonToCamelCase(programsRaw)
		let programsTyped = yield CoCo.apiFetchNowPlaying()
		//console.debug("programsForNow raw %o, typed %o", programsParsed, programsTyped)
		return programsTyped
	})

	eventDetailsAsync = flow(function* (eventId, eventDetailsMode, displayError = true) {
		const start = performance.now()
		const result = yield CoCo.fetchEventDetails(eventId, eventDetailsMode)
		const stop = performance.now()
		result.mode = eventDetailsMode
		//console.debug("eventDetailsCoco %s, mode %s, duration %s, details %o", eventId, eventDetailsMode === CoCo.EventDetailsMode.Lite ? "Lite" : "Full", (stop - start).toFixed(3) + "ms", result)
		if (eventDetailsMode === CoCo.EventDetailsMode.Full ) console.debug("eventDetailsCoco nextEpisodeId %s", result.nextEpisodeId)

		return result
	})
}
